import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, SxProps, Theme } from "@mui/material";
import { StateHandle } from "StateHandle";
import Caption from "components/Caption";
import PageNumberInput from "components/PageNumberInput";
import { ChangeEvent, FC, createRef } from "react";
import { P } from "theme/typography";

interface Props {
  pageRangeState: StateHandle<[number, number] | undefined>;
  page: number;
  docPageCount: number;
  showReferenceType: boolean;
  referenceTypeState: StateHandle<"page" | "page_range">;
  pageRangeSX?: SxProps<Theme>;
  hideReferenceLabel?: boolean;
  dataPendo?: string;
}

function getPageRangeCount(range: [number, number]): number {
  return range[1] - range[0] + 1;
}

const ValueGroupReferencePagePicker: FC<Props> = ({
  pageRangeState,
  page,
  docPageCount,
  showReferenceType,
  referenceTypeState,
  pageRangeSX,
  hideReferenceLabel = false,
  dataPendo,
}) => {
  const startPageInputRef = createRef<HTMLInputElement>();
  const endPageInputRef = createRef<HTMLInputElement>();
  const [referenceType, setReferenceType] = referenceTypeState;
  const [pageRange, setPageRange] = pageRangeState;
  const pageRangeCount = pageRange ? getPageRangeCount(pageRange) : undefined;

  const setPageRangeStart = (x: number) => {
    const end = pageRange?.[1];
    if (x > 0 && x <= docPageCount && x < (end || 1000000)) {
      setPageRange((prev) => (prev ? [x, prev[1]] : undefined));
    }
  };
  const setPageRangeEnd = (x: number) => {
    const start = pageRange?.[0];
    if (x > 0 && x <= docPageCount && x >= (start || 0)) {
      setPageRange((prev) => (prev ? [prev[0], x] : undefined));
    }
  };

  return (
    <>
      {showReferenceType && (
        <FormControl sx={{ mr: 1 }}>
          {!hideReferenceLabel && <FormLabel id="page-or-text">Reference</FormLabel>}
          <RadioGroup
            value={referenceType}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const newType = event.target.value as typeof referenceType;
              if (newType === "page") {
                setPageRange(undefined);
              } else {
                const plusOne = page + 1;
                setPageRange([page, plusOne]);
              }
              setReferenceType(newType);
            }}
            row
            aria-labelledby="page-or-text"
            name="evidence-group">
            <FormControlLabel
              data-pendo={`${dataPendo}-single-page`}
              value="page"
              control={<Radio />}
              label="Single Page"
            />
            <FormControlLabel
              data-pendo={`${dataPendo}-page-range`}
              value="page_range"
              control={<Radio />}
              label="Page Range"
            />
          </RadioGroup>
        </FormControl>
      )}
      {referenceType === "page_range" && pageRange && (
        <Box sx={{ position: "relative", width: 200 }}>
          <Caption
            bold
            text={pageRange ? `${pageRangeCount} page${pageRangeCount !== 1 ? "s" : ""}` : `Select page(s)`}
            sx={{ position: "absolute", top: 0, left: 0 }}
          />
          <Box sx={{ position: "relative", top: 26, display: "flex", ...pageRangeSX }}>
            <PageNumberInput
              dataPendo={`${dataPendo}-start-page`}
              updateNumber={(v) => setPageRangeStart(v)}
              value={pageRange[0]}
              min={1}
              max={page}
              inputRef={startPageInputRef}
            />
            <P sx={{ marginX: "2px" }}>-</P>
            <PageNumberInput
              dataPendo={`${dataPendo}-end-page`}
              updateNumber={(v) => setPageRangeEnd(v)}
              value={pageRange[1]}
              min={page}
              max={docPageCount}
              inputRef={endPageInputRef}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default ValueGroupReferencePagePicker;
